/**
 * ActiveSnoozesPanel - List of all active snoozes for the organization
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PauseCircle, X, Clock } from 'lucide-react';
import type { Snooze } from '@/services/api/ecmReminders';

interface ActiveSnoozesPanelProps {
  snoozes: Snooze[];
  loading: boolean;
  onCancelSnooze: (snoozeId: number) => void;
}

const REASON_LABELS: Record<string, string> = {
  hospitalized: 'Hospitalized',
  unreachable: 'Unreachable',
  vacation: 'Vacation',
  incarcerated: 'Incarcerated',
  member_request: 'Member Request',
  pending_transfer: 'Pending Transfer',
  bereavement: 'Bereavement',
  other: 'Other',
};

export const ActiveSnoozesPanel: React.FC<ActiveSnoozesPanelProps> = ({
  snoozes,
  loading,
  onCancelSnooze,
}) => {
  if (snoozes.length === 0 && !loading) return null;

  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <PauseCircle className="w-4 h-4 text-gray-500" />
          Active Snoozes ({snoozes.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {snoozes.map((snooze) => {
            const expiresAt = new Date(snooze.snooze_until);
            const daysLeft = Math.ceil(
              (expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
            );

            return (
              <div
                key={snooze.id}
                className="flex items-center justify-between px-4 py-2.5 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    Member #{snooze.member_id}
                  </span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-gray-500">
                      {REASON_LABELS[snooze.reason] || snooze.reason}
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-0.5">
                      <Clock className="w-3 h-3" />
                      {daysLeft > 0 ? `${daysLeft}d left` : 'Expiring'}
                    </span>
                  </div>
                  {snooze.reason_details && (
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {snooze.reason_details}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-gray-400 hover:text-red-600"
                  onClick={() => onCancelSnooze(snooze.id)}
                  title="Cancel snooze"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
